<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Card, Table, Spin, message } from 'ant-design-vue';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons-vue';
import {
    getConsultationWorkspace,
    getConclusionByVersion
} from '@/api/consultation-workspace/consultationWorkspaceApi';
import { getVersion } from '@/api/finance-version/getVersion';
import { getTypes } from '@/api/finance/types';
import FinanceStatusBanner from '@/components/finance-version/FinanceStatusBanner.vue';
import { buildConclusionWorkflow, buildVersionWorkflowSteps } from '@/utils/versionWorkflow';

const route = useRoute();
const router = useRouter();
const consultationId = computed(() => Number(route.query.consultation_id || 0));
const userId = computed(() => route.params.userId || route.query.user_id);
const versionId = computed(() => Number(route.params.version || route.params.id || 0));
const loading = ref(true);
const inventory = ref(null);
const consultation = ref(null);
const workspace = ref(null);
const typeNames = ref({});
const comparison = ref([]);

// 僅顯示可由盤點／諮詢數據算出的段落；固定範例建議文先隱藏
const sections = reactive({
    status: '',
    effect: ''
});
const sectionRows = [
    ['status', '一、學員目前狀態'],
    ['effect', '二、預期效果']
];
const visibleSectionRows = computed(() =>
    sectionRows.filter(([key]) => String(sections[key] || '').trim() !== '')
);
const money = (v) => Number(v || 0).toLocaleString('zh-TW');
const printGeneratedAt = computed(() =>
    new Date().toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    })
);
const memberName = computed(
    () =>
        consultation.value?.user_name ||
        inventory.value?.user_name ||
        workspace.value?.user_name ||
        workspace.value?.member_name ||
        ''
);
const printPage = () => {
    const prev = document.title;
    const name = memberName.value ? `｜${memberName.value}` : '';
    document.title = `諮詢結論${name}`;
    window.print();
    setTimeout(() => {
        document.title = prev;
    }, 500);
};

const goBack = () => {
    if (consultationId.value && userId.value) {
        router.push({
            path: `/consultation-workspace/clients/${userId.value}`,
            query: { consultation_id: consultationId.value }
        });
        return;
    }
    if (window.history.length > 1) router.back();
    else router.push('/consultation-workspace/bookings');
};

const totalsFromSheet = (sheet = {}) => ({
    totalAssets: Number(sheet.total_assets ?? 0),
    totalLiabilities: Number(sheet.total_liabilities ?? 0),
    netAssetValue: Number(sheet.net_asset_value ?? 0),
    monthlyIncome: Number(sheet.total_monthly_income ?? 0),
    monthlyExpenses: Number(sheet.total_monthly_expenses ?? 0),
    monthlyCashFlow: Number(sheet.monthly_cash_flow ?? 0)
});

const summaryTotals = computed(() =>
    totalsFromSheet(consultation.value || inventory.value || workspace.value?.post_consultation || workspace.value?.inventory || {})
);

const categoryDiffs = computed(() => {
    if (comparison.value.length) return comparison.value;
    const rows = (sheet) =>
        Array.isArray(sheet?.type_data)
            ? sheet.type_data
            : Object.values(sheet?.type_data || {}).flatMap((g) =>
                  Array.isArray(g) ? g : Object.values(g || {})
              );
    const map = (sheet) =>
        rows(sheet).reduce((out, row) => {
            const key = String(row.type_id ?? row.type_name ?? '');
            const item = out[key] || {
                name: row.type_name || typeNames.value[key] || `分類 ${key}`,
                amount: 0,
                monthly: 0
            };
            item.amount += Number(row.amount ?? row.total_amount ?? 0);
            item.monthly += Number(row.monthly_interest_expense ?? row.total_monthly_interest_expense ?? 0);
            out[key] = item;
            return out;
        }, {});
    const a = map(inventory.value);
    const b = map(consultation.value);
    return [...new Set([...Object.keys(a), ...Object.keys(b)])].map((key) => ({
        name: b[key]?.name || a[key]?.name || key,
        inventory: a[key] || { amount: 0, monthly: 0 },
        consultation: b[key] || { amount: 0, monthly: 0 }
    }));
});

const diffTableRows = computed(() =>
    categoryDiffs.value.map((row) => {
        const inv = Number(row.inventory?.amount || 0);
        const con = Number(row.consultation?.amount || 0);
        const delta = con - inv;
        return {
            key: row.type_id || row.name,
            name: row.type_name || row.name,
            inventory: inv,
            inventoryMonthly: Number(row.inventory?.monthly || 0),
            consultation: con,
            consultationMonthly: Number(row.consultation?.monthly || 0),
            delta,
            statement: row.statement || (delta === 0 ? '無變動' : delta > 0 ? `增加 ${money(delta)}` : `減少 ${money(Math.abs(delta))}`)
        };
    })
);

const diffColumns = [
    { title: '分類', dataIndex: 'name', key: 'name' },
    { title: '盤點版', key: 'inventory', align: 'right' },
    { title: '諮詢版', key: 'consultation', align: 'right' },
    { title: '差異', dataIndex: 'statement', key: 'statement' }
];

const workflowSteps = computed(() => buildVersionWorkflowSteps(workspace.value || {}));

const openWorkflowStep = (step) => {
    if (!step || step.key === 'conclusion') return;
    const id = Number(step.data?.id || 0);
    if (!id) return message.info('此版本尚未建立');
    router.push({
        name: step.editable ? 'ConsultationWorkspaceVersionEdit' : 'ConsultationWorkspaceVersionView',
        params: { id },
        query: { consultation_id: consultationId.value, user_id: userId.value }
    });
};

const changeWorkflowStep = (index) => openWorkflowStep(workflowSteps.value[index]);

const load = async () => {
    loading.value = true;
    try {
        if (!versionId.value) return;
        const conclusionRes = await getConclusionByVersion(versionId.value, consultationId.value);
        if (conclusionRes?.status === 1) {
            Object.assign(sections, conclusionRes.data?.sections || {});
            comparison.value = Array.isArray(conclusionRes.data?.comparison)
                ? conclusionRes.data.comparison
                : [];
            workspace.value = buildConclusionWorkflow(conclusionRes.data || {});
        }
        if (!consultationId.value) return;
        const [workspaceRes, typesRes] = await Promise.all([
            getConsultationWorkspace(consultationId.value),
            getTypes({ page_size: 100 })
        ]);
        const typeList = typesRes?.data || typesRes || [];
        typeNames.value = Object.fromEntries(
            (Array.isArray(typeList) ? typeList : []).map((type) => [String(type.id), type.name])
        );
        const w = workspaceRes?.data || {};
        workspace.value = w;
        const inventoryId = Number(w.inventory?.id || 0);
        const consultationSheetId = Number(w.post_consultation?.id || 0);
        const [a, b] = await Promise.all([
            inventoryId ? getVersion(inventoryId) : Promise.resolve(null),
            consultationSheetId ? getVersion(consultationSheetId) : Promise.resolve(null)
        ]);
        inventory.value = a?.data || a || null;
        consultation.value = b?.data || b || null;
    } catch (e) {
        console.error(e);
        message.error('無法載入諮詢結論');
    } finally {
        loading.value = false;
    }
};
onMounted(load);
</script>

<template>
    <div class="conclusion-page admin-editor-page">
        <header class="cw-topbar no-print">
            <div class="cw-topbar__nav">
                <button type="button" class="cw-topbar__back" @click="goBack">
                    <ArrowLeftOutlined />
                    場次
                </button>
                <span class="cw-topbar__sep">/</span>
                <span class="cw-topbar__title">諮詢結論</span>
            </div>
            <a-button class="admin-editor-btn admin-editor-btn--export" @click="printPage">
                <template #icon><PrinterOutlined /></template>
                列印 / 匯出 PDF
            </a-button>
        </header>

        <div class="conclusion-print-doc">
            <header class="conclusion-print-header">
                <div>
                    <p class="conclusion-print-header__eyebrow">財富諮詢結論</p>
                    <h1 class="conclusion-print-header__title">諮詢結論報告</h1>
                    <p v-if="memberName" class="conclusion-print-header__member">
                        學員：{{ memberName }}
                    </p>
                </div>
                <div class="conclusion-print-header__meta">
                    <div>{{ printGeneratedAt }}</div>
                </div>
            </header>

            <FinanceStatusBanner
                v-if="workspace"
                class="no-print"
                :totals="summaryTotals"
                :steps="workflowSteps"
                :current-step="3"
                @step-change="changeWorkflowStep"
            />

            <Spin :spinning="loading">
                <Card
                    v-if="visibleSectionRows.length"
                    class="admin-panel conclusion-card"
                    :bordered="false"
                >
                    <div
                        v-for="([key, label]) in visibleSectionRows"
                        :key="key"
                        class="conclusion-row"
                    >
                        <strong>{{ label }}</strong>
                        <div class="conclusion-value">{{ sections[key] }}</div>
                    </div>
                </Card>

                <Card
                    class="admin-panel conclusion-card conclusion-card--diff"
                    :bordered="false"
                    title="三、盤點版／諮詢版差異"
                >
                    <Table
                        v-if="diffTableRows.length"
                        class="admin-table-surface conclusion-diff-table"
                        :columns="diffColumns"
                        :data-source="diffTableRows"
                        :pagination="false"
                        size="middle"
                        row-key="key"
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'inventory' || column.key === 'consultation'">
                                <div class="diff-amount">
                                    {{ money(column.key === 'inventory' ? record.inventory : record.consultation) }}
                                </div>
                                <div
                                    v-if="(column.key === 'inventory' ? record.inventoryMonthly : record.consultationMonthly) !== 0"
                                    class="diff-monthly"
                                >
                                    月 {{ money(column.key === 'inventory' ? record.inventoryMonthly : record.consultationMonthly) }}
                                </div>
                            </template>
                        </template>
                    </Table>
                    <div v-else class="conclusion-empty">尚無可比較的分類資料</div>
                </Card>
            </Spin>

            <footer class="conclusion-print-footer">
                本報告由系統依盤點版／諮詢版整理產出，供諮詢紀錄使用。
            </footer>
        </div>
    </div>
</template>

<style scoped>
.conclusion-page {
    width: 100%;
    max-width: none;
}
.cw-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
}
.cw-topbar__nav {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}
.cw-topbar__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #8b6914;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.cw-topbar__back:hover {
    color: #6b5428;
}
.cw-topbar__sep {
    color: #cbd5e1;
    font-size: 12px;
}
.cw-topbar__title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}
.conclusion-print-header,
.conclusion-print-footer {
    display: none;
}
.conclusion-card {
    margin-bottom: 14px;
}
.conclusion-row {
    display: grid;
    grid-template-columns: 148px minmax(0, 1fr);
    gap: 12px 18px;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;
}
.conclusion-row:last-child {
    border-bottom: 0;
    padding-bottom: 0;
}
.conclusion-row:first-child {
    padding-top: 0;
}
.conclusion-row strong {
    color: #475569;
    font-size: 13px;
    font-weight: 700;
}
.conclusion-value {
    color: #0f172a;
    line-height: 1.65;
    white-space: pre-wrap;
}
.diff-amount {
    font-weight: 700;
    color: #0f172a;
}
.diff-monthly {
    color: #64748b;
    font-size: 12px;
}
.conclusion-empty {
    padding: 16px 0;
    color: #64748b;
    text-align: center;
}
@media (max-width: 700px) {
    .cw-topbar {
        align-items: flex-start;
        flex-direction: column;
    }
    .conclusion-row {
        grid-template-columns: 1fr;
        gap: 4px;
    }
}
@media print {
    .conclusion-page {
        max-width: none;
        color: #111;
        background: #fff;
    }
    .conclusion-print-doc {
        max-width: none;
    }
    .conclusion-print-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 18px;
        padding-bottom: 12px;
        border-bottom: 2px solid #1f2937;
    }
    .conclusion-print-header__eyebrow {
        margin: 0 0 4px;
        font-size: 11px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #6b7280;
    }
    .conclusion-print-header__title {
        margin: 0;
        font-size: 22px;
        font-weight: 800;
        letter-spacing: 0.02em;
        color: #111827;
    }
    .conclusion-print-header__member {
        margin: 6px 0 0;
        font-size: 13px;
        color: #374151;
    }
    .conclusion-print-header__meta {
        font-size: 11px;
        color: #6b7280;
        text-align: right;
        white-space: nowrap;
    }
    .conclusion-print-footer {
        display: block;
        margin-top: 18px;
        padding-top: 10px;
        border-top: 1px solid #d1d5db;
        font-size: 10px;
        color: #6b7280;
    }
    .conclusion-card {
        margin-bottom: 16px;
        break-inside: avoid;
        box-shadow: none !important;
        border: 1px solid #d1d5db !important;
        border-radius: 8px !important;
        background: #fff !important;
    }
    .conclusion-card :deep(.ant-card-head) {
        min-height: auto;
        padding: 10px 14px;
        border-bottom: 1px solid #e5e7eb;
        background: #f9fafb !important;
    }
    .conclusion-card :deep(.ant-card-head-title) {
        padding: 0;
        font-size: 14px;
        font-weight: 700;
        color: #111827;
    }
    .conclusion-card :deep(.ant-card-body) {
        padding: 12px 14px !important;
    }
    .conclusion-row {
        grid-template-columns: 150px minmax(0, 1fr);
        gap: 8px 16px;
        padding: 9px 0;
        border-bottom-color: #e5e7eb;
        break-inside: avoid;
    }
    .conclusion-row strong {
        font-size: 12px;
        color: #374151;
    }
    .conclusion-value {
        font-size: 12.5px;
        line-height: 1.55;
        color: #111827;
    }
    .conclusion-diff-table :deep(.ant-table) {
        font-size: 12px;
    }
    .conclusion-diff-table :deep(.ant-table-thead > tr > th) {
        background: #f3f4f6 !important;
        color: #111827 !important;
        font-weight: 700;
        border-bottom: 1px solid #d1d5db !important;
        padding: 8px 10px !important;
    }
    .conclusion-diff-table :deep(.ant-table-tbody > tr > td) {
        border-bottom: 1px solid #e5e7eb !important;
        padding: 8px 10px !important;
        color: #111827;
    }
    .diff-amount {
        font-size: 12.5px;
    }
    .diff-monthly {
        font-size: 11px;
        color: #4b5563;
    }
    .conclusion-empty {
        padding: 8px 0;
        font-size: 12px;
    }
}
</style>
